import OpusMediaRecorder from 'opus-media-recorder';
import React from 'react';
import useAsrSettings from '../../../useSession/useAsrSettings';
import arrayBufferToBase64 from './utils/arrayBufferToBase64';
import { AudioConfig } from '../../../useAudioConfig';
import config from '../../../../../../config';

let baseUrl = config.BaseUrl;
if (baseUrl.endsWith('/')) { baseUrl = baseUrl.slice(0, -1); }

const options = { mimeType: 'audio/ogg', audioBitsPerSecond: 96000 };
const workerOptions = {
  encoderWorkerFactory() {
    return new Worker(`${baseUrl}/wasm/encoderWorker.umd.js`);
  },
  OggOpusEncoderWasmPath: `${baseUrl}/wasm/OggOpusEncoder.wasm`,
  WebMOpusEncoderWasmPath: `${baseUrl}/wasm/WebMOpusEncoder.wasm`,
};

let stream: MediaStream | null = null;
let mediaRecorder: any | null = null;

function stopAudioCapture() {
  if (mediaRecorder) {
    if (mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
  }
  if (stream) {
    stream.getTracks().forEach((track) => track.stop());
  }
}

async function startStream(audioConfig: AudioConfig, callback: (data: string) => void) {
  await navigator.mediaDevices
    .getUserMedia({
      audio: {
        deviceId: audioConfig.deviceId,
        sampleRate: 16000,
        noiseSuppression: audioConfig.noiseSuppression,
        echoCancellation: audioConfig.echoCancellation,
        autoGainControl: audioConfig.autoGainControl,
      },
      video: false,
    })
    .then((value) => {
      stream = value;
      mediaRecorder = new OpusMediaRecorder(value, options, workerOptions);
    });

  let header: ArrayBuffer | null = null;
  mediaRecorder.ondataavailable = async (e: BlobEvent) => {
    const buffer = await e.data.arrayBuffer();
    if (!header) { header = buffer.slice(0, 129); }
    if (buffer.byteLength > 0) {
      const bufferWithHeader = await new Blob([header, buffer], { type: 'audio/ogg; codecs=opus' }).arrayBuffer();
      callback(arrayBufferToBase64(bufferWithHeader));
    }
  };
  mediaRecorder.start(150);
}

/**
 * @deprecated Use new audio capture hook
 */
export default function useLegacyAudioCapture(inProgress: boolean, audioConfig: AudioConfig) {
  const [lastAudioBlob, setLastAudioBlob] = React.useState<string | null>(null);
  const { enableCoreAsrApi: settingsEnabled } = useAsrSettings();
  const prevDeviceId = React.useRef(audioConfig.deviceId);

  const enabled = React.useMemo(() => inProgress && settingsEnabled, [inProgress, settingsEnabled]);

  React.useEffect(() => {
    if (prevDeviceId.current !== audioConfig.deviceId && enabled) {
      stopAudioCapture();
      startStream(audioConfig, (blob) => { setLastAudioBlob(blob); });
    }
    prevDeviceId.current = audioConfig.deviceId;
  }, [audioConfig.deviceId, enabled]);

  React.useEffect(() => {
    if (enabled) {
      startStream(audioConfig, (blob) => { setLastAudioBlob(blob); });
    }
    return () => {
      stopAudioCapture();
    };
  }, [enabled]);

  return lastAudioBlob;
}
